import * as THREE from 'three';
import { PointShape } from './defines';
import { LRU } from './LRU';
import { PointCloudMaterial } from './materials/PointCloudMaterial';
import { PointCloudOctreeGeometryNode } from './PointCloudOctreeGeometry';
import { PointCloudTree, PointCloudTreeNode } from './PointCloudTree';
import { Utils } from './utils';
import type { Subscription } from '@pointorama/potree-shared';
import {
  getPointCloudGradientProperty,
  getPointCloudPointSizeProperty,
  getPointCloudAppearanceProperty,
  getPointCloudResolutionProperty,
  getPointCloudHeightRangeProperty,
  getPointCloudIntensityRangeProperty,
  getPointCloudHiddenClassificationsProperty,
  getPointCloudHiddenSourcesProperty,
  hasGlobalPropertyService,
} from '@pointorama/potree-shared';
import { changePointCloudGradient, changePointCloudAppearance, changePointCloudResolution, EnumAppearanceTypes } from './pointCloudHelpers';
import type { GradientType } from '../modules/Properties/types';

const measureTimings = false;

/** Buffer interface for point cloud geometry node data */
interface PointBuffer {
  offset(name: string): number;
  stride: number;
  data: ArrayBuffer;
  numElements: number;
}

/** Geometry type used by PointCloudOctreeGeometry */
interface PCOGeometry {
  type?: string;
  spacing: number;
  boundingBox: THREE.Box3;
  tightBoundingBox?: THREE.Box3;
  offset: THREE.Vector3;
  root: PointCloudOctreeGeometryNode;
  pointAttributes: {
    attributes: Array<{
      name: string;
      range: [number | number[], number | number[]];
      initialRange: [number, number];
    }>;
  };
  projection?: string;
  fallbackProjection?: string;
  minDifference?: number;
  maxDifference?: number;
  onAttributeChange?: (attributeName: string, allClassificationsVisible: boolean) => void;
  /** Depth of the octree hierarchy (number of levels) */
  depth?: number;
  /** Scale factor for point cloud resolution calculations */
  scale?: number;
  /** Cached data chunk map (PPCPointCloudGeometry stores chunks here) */
  dataChunkCache?: Map<string, { level: number; pointsAmount: number }>;
  /** Dispose method for cleaning up geometry resources */
  dispose?(): void;
}

/** Viewer global - used in reload() */
declare const viewer: { allClassificationsVisible: boolean };

/** Pick params interface */
interface PickParams {
  pickWindowSize?: number;
  pickOutsideClipRegion?: boolean;
  width?: number;
  height?: number;
  pointSizeType?: number;
  pointSize?: number;
  x?: number;
  y?: number;
  pickClipped?: boolean;
  all?: boolean;
}

/** Viewer interface for pick method */
interface PickViewer {
  renderer: THREE.WebGLRenderer;
  pRenderer: {
    renderOctree(octree: PointCloudOctree, nodes: PointCloudOctreeNode[], camera: THREE.Camera, target: THREE.WebGLRenderTarget): void;
  };
}

/** SDF (signed distance field) structure */
interface SignedDistanceField {
  resolution: number;
  field: Float32Array;
}

/** Pick state for GPU picking */
interface PickState {
  renderTarget: THREE.WebGLRenderTarget;
  material: PointCloudMaterial;
  scene: THREE.Scene;
}

/** Potree buffer attribute with optional potree metadata */
interface PotreeBufferAttribute extends THREE.BufferAttribute {
  potree?: { scale: number; offset: number };
}

/** Pick result point with known fields and dynamic geometry attributes */
interface PickPoint {
  position?: THREE.Vector3;
  pointCloudId?: string;
  distanceToRay?: number;
  spacing?: number;
  level?: number | null;
  /** Dynamic geometry attributes (e.g. color, intensity, classification) */
  attributes: Record<string, THREE.Vector3 | number[]>;
}

/** Pick hit result */
interface PickHit {
  pIndex: number;
  pcIndex: number;
  distanceToCenter: number;
  point?: PickPoint;
}

export class PointCloudOctreeNode extends PointCloudTreeNode {
  children: Array<PointCloudOctreeNode | PointCloudOctreeGeometryNode>;
  sceneNode: THREE.Points | null;
  octree: PointCloudOctree | null;
  geometryNode!: PointCloudOctreeGeometryNode & { buffer: PointBuffer; density?: number; estimatedSpacing?: number };
  pointcloud!: PointCloudOctree;
  pcIndex?: number;
  debug?: boolean;
  refinedByAllChildren?: boolean;

  constructor() {
    super();

    this.children = [];
    this.sceneNode = null;
    this.octree = null;
  }

  getNumPoints() {
    return this.geometryNode.numPoints;
  }

  isLoaded() {
    return true;
  }

  isTreeNode() {
    return true;
  }

  isGeometryNode() {
    return false;
  }

  getLevel() {
    return this.geometryNode.level;
  }

  getBoundingSphere() {
    return this.geometryNode.boundingSphere;
  }

  getBoundingBox() {
    return this.geometryNode.boundingBox;
  }

  getChildren() {
    const children: Array<PointCloudOctreeNode | PointCloudOctreeGeometryNode> = [];

    for (let i = 0; i < 8; i++) {
      if (this.children[i]) {
        children.push(this.children[i]);
      }
    }

    return children;
  }

  getPointsInBox(boxNode: THREE.Object3D) {
    if (!this.sceneNode) {
      return null;
    }

    const buffer = this.geometryNode.buffer;

    const posOffset = buffer.offset('position');
    const stride = buffer.stride;
    const view = new DataView(buffer.data);

    const worldToBox = boxNode.matrixWorld.clone().invert();
    const objectToBox = new THREE.Matrix4().multiplyMatrices(worldToBox, this.sceneNode.matrixWorld);

    const inBox: THREE.Vector3[] = [];

    const pos = new THREE.Vector4();
    for (let i = 0; i < buffer.numElements; i++) {
      const x = view.getFloat32(i * stride + posOffset + 0, true);
      const y = view.getFloat32(i * stride + posOffset + 4, true);
      const z = view.getFloat32(i * stride + posOffset + 8, true);

      pos.set(x, y, z, 1);
      pos.applyMatrix4(objectToBox);

      if (-0.5 < pos.x && pos.x < 0.5) {
        if (-0.5 < pos.y && pos.y < 0.5) {
          if (-0.5 < pos.z && pos.z < 0.5) {
            pos.set(x, y, z, 1).applyMatrix4(this.sceneNode.matrixWorld);
            inBox.push(new THREE.Vector3(pos.x, pos.y, pos.z));
          }
        }
      }
    }

    return inBox;
  }

  get name() {
    return this.geometryNode.name;
  }
}

export class PointCloudOctree extends PointCloudTree {
  pointBudget: number;
  pcoGeometry: PCOGeometry;
  boundingBox: THREE.Box3;
  boundingSphere: THREE.Sphere;
  material: PointCloudMaterial;
  visiblePointsTarget: number;
  minimumNodePixelSize: number;
  level: number;
  lru: LRU;
  downSamplingLevel: number;
  resolvedPoints: number;
  segmentList: unknown[];
  showBoundingBox: boolean;
  boundingBoxNodes: THREE.Object3D[];
  loadQueue: unknown[];
  visibleBounds: THREE.Box3;
  override visibleNodes: PointCloudOctreeNode[];
  visibleGeometry: PointCloudOctreeGeometryNode[];
  generateDEM: boolean;
  _visible: boolean;
  projection?: string;
  fallbackProjection?: string;
  override root: PointCloudOctreeNode | PointCloudOctreeGeometryNode | null;
  visibleNodeTextureOffsets?: Map<PointCloudOctreeNode, number>;
  pickState?: PickState;
  signedDistanceField?: SignedDistanceField;
  identifier?: string;
  numVisiblePoints = 0;
  debugDIV?: unknown;

  // Backref set by Scene.addPointCloud. Required by viewer-dependent reactive subscriptions
  // (hidden classifications/sources). Window.viewer is NOT a runtime global in the main app.
  viewer?: typeof window.viewer;

  // Reactive property subscriptions — wired up in bindReactiveProperties(), torn down in disposeReactiveProperties()
  private _gradientSub?: Subscription;
  private _pointSizeSub?: Subscription;
  private _appearanceSub?: Subscription;
  private _resolutionSub?: Subscription;
  private _heightRangeSub?: Subscription;
  private _intensityRangeSub?: Subscription;
  private _hiddenClassificationsSub?: Subscription;
  private _hiddenSourcesSub?: Subscription;

  constructor(geometry: PCOGeometry, material: PointCloudMaterial | undefined) {
    super();

    this.pointBudget = Infinity;
    this.pcoGeometry = geometry;
    this.boundingBox = this.pcoGeometry.boundingBox;
    this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
    this.material = material || new PointCloudMaterial();
    this.visiblePointsTarget = 2 * 1000 * 1000;
    this.minimumNodePixelSize = 150;
    this.level = 0;
    this.position.copy(geometry.offset);
    this.updateMatrix();
    this.lru = new LRU();
    this.downSamplingLevel = Infinity;
    this.resolvedPoints = 0;
    this.segmentList = [];

    const onAttributeChange = () => {
      this.reload();
    };
    // @ts-expect-error - PointCloudMaterial dispatches 'active_attribute_changed' events at runtime
    this.material.addEventListener('active_attribute_changed', onAttributeChange);

    {
      const priorityQueue = ['rgba', 'rgb', 'intensity', 'classification'];
      let selected = 'rgba';

      for (const attributeName of priorityQueue) {
        const attribute = this.pcoGeometry.pointAttributes.attributes.find((a) => a.name === attributeName);

        if (!attribute) {
          continue;
        }

        const min = attribute.range[0].constructor.name === 'Array' ? attribute.range[0] : [attribute.range[0]];
        const max = attribute.range[1].constructor.name === 'Array' ? attribute.range[1] : [attribute.range[1]];

        const range_min = new THREE.Vector3(...(min as number[]));
        const range_max = new THREE.Vector3(...(max as number[]));
        const range = range_min.distanceTo(range_max);

        if (range === 0) {
          continue;
        }

        selected = attributeName;
        break;
      }
      this.material.activeAttributeName = selected;
    }

    this.showBoundingBox = false;
    this.boundingBoxNodes = [];
    this.loadQueue = [];
    this.visibleBounds = new THREE.Box3();
    this.visibleNodes = [];
    this.visibleGeometry = [];
    this.generateDEM = false;
    this.name = '';
    this._visible = true;

    {
      let box = [this.pcoGeometry.tightBoundingBox, this.getBoundingBoxWorld()].find((v) => v !== undefined);

      this.updateMatrixWorld(true);
      box = Utils.computeTransformedBoundingBox(box ?? this.getBoundingBoxWorld(), this.matrixWorld);

      const bMin = box.min.z;
      const bMax = box.max.z;
      this.material.heightMin = bMin;
      this.material.heightMax = bMax;
      // Pythagoras
      this.material.initialHeightMin = bMin;
      this.material.initialHeightMax = bMax;
      // @ts-expect-error - PointCloudMaterial has differenceMin/Max properties at runtime
      this.material.differenceMin = this.pcoGeometry.minDifference;
      // @ts-expect-error - PointCloudMaterial has differenceMin/Max properties at runtime
      this.material.differenceMax = this.pcoGeometry.maxDifference;
    }

    // TODO read projection from file instead
    this.projection = geometry.projection;
    this.fallbackProjection = geometry.fallbackProjection;

    this.root = this.pcoGeometry.root;
  }

  // Pythagoras
  reload() {
    if (this.pcoGeometry.type === 'ppc') {
      if (!this.pcoGeometry.onAttributeChange || !this.material.activeAttributeName) return;
      this.pcoGeometry.onAttributeChange(this.material.activeAttributeName, viewer.allClassificationsVisible);
    }
  }

  /**
   * Wire reactive property subscriptions for this point cloud.
   *
   * For each property: read the current value, apply it to material/state, then `subscribeToChanges`
   * so subsequent value updates flow through to the material. Idempotent — disposes any existing
   * subscriptions first.
   *
   * Must be called AFTER `identifier` is assigned (e.g. in Potree.load*). Bounds-dependent properties
   * (`heightRange`, `intensityRange`) read `material.initial{Height,Intensity}{Min,Max}` which are set
   * synchronously in the constructor.
   */
  bindReactiveProperties(): void {
    if (!this.identifier) return;
    if (!hasGlobalPropertyService()) return;

    // Idempotency: tear down any prior subscriptions before re-binding
    this.disposeReactiveProperties();

    const id = this.identifier;
    const material = this.material;

    // --- Gradient ---
    const gradientProp = getPointCloudGradientProperty(id);
    if (gradientProp.value) changePointCloudGradient({ pointCloud: this, gradient: gradientProp.value as GradientType });
    this._gradientSub = gradientProp.subscribeToChanges((value) => {
      changePointCloudGradient({ pointCloud: this, gradient: value as GradientType });
    });

    // --- Point size ---
    const pointSizeProp = getPointCloudPointSizeProperty(id);
    if (pointSizeProp.value !== undefined) material.size = pointSizeProp.value;
    this._pointSizeSub = pointSizeProp.subscribeToChanges((value) => {
      material.size = value;
    });

    // --- Appearance ---
    const appearanceProp = getPointCloudAppearanceProperty(id);
    if (appearanceProp.value) changePointCloudAppearance({ pointCloud: this, value: appearanceProp.value as EnumAppearanceTypes });
    this._appearanceSub = appearanceProp.subscribeToChanges((value) => {
      changePointCloudAppearance({ pointCloud: this, value: value as EnumAppearanceTypes });
    });

    // --- Resolution (-1 sentinel = no user choice; don't apply) ---
    const resolutionProp = getPointCloudResolutionProperty(id);
    if (resolutionProp.value !== undefined && resolutionProp.value !== -1) {
      changePointCloudResolution({ pointCloud: this, value: resolutionProp.value });
    }
    this._resolutionSub = resolutionProp.subscribeToChanges((value) => {
      const resolved = value === null || value === undefined ? Infinity : (value as number);
      if (resolved === -1) return;
      changePointCloudResolution({ pointCloud: this, value: resolved });
    });

    // --- Height range (bounds-dependent — registers lazily with the actual initial bounds) ---
    if (material.initialHeightMin !== undefined && material.initialHeightMax !== undefined) {
      const heightRangeProp = getPointCloudHeightRangeProperty(id, material.initialHeightMin, material.initialHeightMax);
      const hr = heightRangeProp.value;
      if (Array.isArray(hr)) {
        material.heightMin = hr[0];
        material.heightMax = hr[1];
      }
      this._heightRangeSub = heightRangeProp.subscribeToChanges((value) => {
        if (!Array.isArray(value)) return;
        material.heightMin = value[0];
        material.heightMax = value[1];
      });
    }

    // --- Intensity range (only when the cloud has intensity data) ---
    if (material.initialIntensityMin !== undefined && material.initialIntensityMax !== undefined) {
      const intensityMin = Math.round(material.initialIntensityMin);
      const intensityMax = Math.round(material.initialIntensityMax);
      const intensityRangeProp = getPointCloudIntensityRangeProperty(id, intensityMin, intensityMax);
      const ir = intensityRangeProp.value;
      if (Array.isArray(ir)) {
        material.intensityMin = ir[0];
        material.intensityMax = ir[1];
      }
      this._intensityRangeSub = intensityRangeProp.subscribeToChanges((value) => {
        if (!Array.isArray(value)) return;
        material.intensityMin = value[0];
        material.intensityMax = value[1];
      });
    }

    // --- Hidden classifications (number[] of class codes) ---
    const hiddenClassificationsProp = getPointCloudHiddenClassificationsProperty(id);
    this._applyHiddenClassifications(hiddenClassificationsProp.value);
    this._hiddenClassificationsSub = hiddenClassificationsProp.subscribeToChanges((value) => {
      this._applyHiddenClassifications(value);
    });

    // --- Hidden sources (number[] of source ids) ---
    const hiddenSourcesProp = getPointCloudHiddenSourcesProperty(id);
    this._applyHiddenSources(hiddenSourcesProp.value);
    this._hiddenSourcesSub = hiddenSourcesProp.subscribeToChanges((value) => {
      this._applyHiddenSources(value);
    });
  }

  /** Tear down all reactive subscriptions. Called by `Scene.removePointCloud`. */
  disposeReactiveProperties(): void {
    this._gradientSub?.unsubscribe();
    this._pointSizeSub?.unsubscribe();
    this._appearanceSub?.unsubscribe();
    this._resolutionSub?.unsubscribe();
    this._heightRangeSub?.unsubscribe();
    this._intensityRangeSub?.unsubscribe();
    this._hiddenClassificationsSub?.unsubscribe();
    this._hiddenSourcesSub?.unsubscribe();
    this._gradientSub = undefined;
    this._pointSizeSub = undefined;
    this._appearanceSub = undefined;
    this._resolutionSub = undefined;
    this._heightRangeSub = undefined;
    this._intensityRangeSub = undefined;
    this._hiddenClassificationsSub = undefined;
    this._hiddenSourcesSub = undefined;
  }

  private _applyHiddenClassifications(value: unknown): void {
    if (!this.identifier) return;
    const v = this.viewer;
    if (!v?.classificationsInfo) return;
    const hidden = Array.isArray(value) ? (value as number[]).slice() : [];
    v.classificationsInfo.hiddenClassesByPointCloud[this.identifier] = hidden;
    // Existing event consumed by useRenderer for re-render; uniforms refresh via per-frame `material.classification = ...` in viewer.update
    v.dispatchEvent({ type: 'classification_visibility_changed', viewer: v });
  }

  private _applyHiddenSources(value: unknown): void {
    if (!this.identifier) return;
    const v = this.viewer;
    if (!v?.sourcesInfo) return;
    const hidden = Array.isArray(value) ? (value as number[]) : [];
    const hiddenSet = new Set<number>(hidden);
    const sources = v.sourcesInfo.getSourceIdsForPointCloud({ pointcloudId: this.identifier });
    for (const source of sources) {
      source.visible = !hiddenSet.has(source.id);
    }
  }

  setName(name: string) {
    if (this.name !== name) {
      this.name = name;
      // @ts-expect-error - custom event type used by Potree event system
      this.dispatchEvent({ type: 'name_changed', name: name, pointcloud: this });
    }
  }

  getName() {
    return this.name;
  }

  getAttribute(name: string) {
    const attribute = this.pcoGeometry.pointAttributes.attributes.find((a) => a.name === name);

    if (attribute) {
      return attribute;
    } else {
      return null;
    }
  }

  getAttributes() {
    return this.pcoGeometry.pointAttributes;
  }

  toTreeNode(geometryNode: PointCloudOctreeGeometryNode, parent: PointCloudOctreeNode | null) {
    const node = new PointCloudOctreeNode();

    const sceneNode = new THREE.Points(geometryNode.geometry ?? new THREE.BufferGeometry(), this.material as unknown as THREE.PointsMaterial);
    sceneNode.name = geometryNode.name;
    sceneNode.position.copy(geometryNode.boundingBox.min);
    sceneNode.frustumCulled = false;
    sceneNode.onBeforeRender = (_this: THREE.WebGLRenderer, _scene: THREE.Scene, _camera: THREE.Camera, _geometry: THREE.BufferGeometry, material: THREE.Material) => {
      // @ts-expect-error - PointCloudMaterial has program property at runtime
      if (material.program) {
        // @ts-expect-error - accessing WebGL program internals
        _this.getContext().useProgram(material.program.program);

        // @ts-expect-error - accessing WebGL uniform map internals
        if (material.program.getUniforms().map.level) {
          const level = geometryNode.getLevel();
          // @ts-expect-error - PointCloudMaterial uniform access
          material.uniforms.level.value = level;
          // @ts-expect-error - accessing WebGL uniform map internals
          material.program.getUniforms().map.level.setValue(_this.getContext(), level);
        }

        // @ts-expect-error - accessing WebGL uniform map internals
        if (this.visibleNodeTextureOffsets && material.program.getUniforms().map.vnStart) {
          const vnStart = this.visibleNodeTextureOffsets?.get(node);
          // @ts-expect-error - PointCloudMaterial uniform access
          material.uniforms.vnStart.value = vnStart;
          // @ts-expect-error - accessing WebGL uniform map internals
          material.program.getUniforms().map.vnStart.setValue(_this.getContext(), vnStart);
        }

        // @ts-expect-error - accessing WebGL uniform map internals
        if (material.program.getUniforms().map.pcIndex) {
          const i = node.pcIndex ? node.pcIndex : this.visibleNodes.indexOf(node);
          // @ts-expect-error - PointCloudMaterial uniform access
          material.uniforms.pcIndex.value = i;
          // @ts-expect-error - accessing WebGL uniform map internals
          material.program.getUniforms().map.pcIndex.setValue(_this.getContext(), i);
        }
      }
    };

    node.geometryNode = geometryNode as PointCloudOctreeNode['geometryNode'];
    node.sceneNode = sceneNode;
    node.pointcloud = this;
    node.children = [];
    for (let i = 0; i < 8; i++) {
      node.children[i] = geometryNode.children[i];
    }

    // Pythagoras
    if (!parent) {
      this.root = node;
      this.add(sceneNode);
    } else {
      const childIndex = parseInt(geometryNode.name[geometryNode.name.length - 1]);
      parent.sceneNode?.add(sceneNode);
      parent.children[childIndex] = node;
    }

    const disposeListener = () => {
      if (parent) {
        const childIndex = parseInt(geometryNode.name[geometryNode.name.length - 1]);
        if (node.sceneNode) parent.sceneNode?.remove(node.sceneNode);
        parent.children[childIndex] = geometryNode;
      } else {
        this.remove(sceneNode);
        this.root = geometryNode;
      }
    };
    geometryNode.oneTimeDisposeHandlers.push(disposeListener);

    return node;
  }

  updateVisibleBounds() {
    const leafNodes: PointCloudOctreeNode[] = [];
    for (let i = 0; i < this.visibleNodes.length; i++) {
      const node = this.visibleNodes[i];
      let isLeaf = true;

      for (let j = 0; j < node.children.length; j++) {
        const child = node.children[j];
        if (child instanceof PointCloudOctreeNode) {
          isLeaf = isLeaf && !(child.sceneNode?.visible ?? false);
        } else if (child instanceof PointCloudOctreeGeometryNode) {
          isLeaf = true;
        }
      }

      if (isLeaf) {
        leafNodes.push(node);
      }
    }

    this.visibleBounds.min = new THREE.Vector3(Infinity, Infinity, Infinity);
    this.visibleBounds.max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    for (let i = 0; i < leafNodes.length; i++) {
      const node = leafNodes[i];

      this.visibleBounds.expandByPoint(node.getBoundingBox().min);
      this.visibleBounds.expandByPoint(node.getBoundingBox().max);
    }
  }

  updateMaterial(material: PointCloudMaterial, _visibleNodes: PointCloudOctreeNode[], camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer) {
    material.fov = camera.fov * (Math.PI / 180);
    material.screenWidth = renderer.domElement.clientWidth;
    material.screenHeight = renderer.domElement.clientHeight;
    material.spacing = this.pcoGeometry.spacing;
    material.near = camera.near;
    material.far = camera.far;
    material.uniforms.octreeSize.value = this.pcoGeometry.boundingBox.getSize(new THREE.Vector3()).x;
  }

  computeVisibilityTextureData(nodes: PointCloudOctreeNode[], _camera: THREE.Camera) {
    if (measureTimings) performance.mark('computeVisibilityTextureData-start');

    const data = new Uint8Array(nodes.length * 4);
    const visibleNodeTextureOffsets = new Map<PointCloudOctreeNode, number>();

    // copy array
    nodes = nodes.slice();

    // sort by level and index, e.g. r, r0, r3, r4, r01, r07, r30, ...
    const sort = function (a: PointCloudOctreeNode, b: PointCloudOctreeNode) {
      const na = a.geometryNode.name;
      const nb = b.geometryNode.name;
      if (na.length !== nb.length) return na.length - nb.length;
      if (na < nb) return -1;
      if (na > nb) return 1;
      return 0;
    };
    nodes.sort(sort);

    const nodeMap = new Map<string, PointCloudOctreeNode>();
    const offsetsToChild = new Array(nodes.length).fill(Infinity);

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      nodeMap.set(node.name, node);
      visibleNodeTextureOffsets.set(node, i);

      if (i > 0) {
        const index = parseInt(node.name.slice(-1));
        const parentName = node.name.slice(0, -1);
        const parent = nodeMap.get(parentName);
        if (!parent) continue;
        const parentOffset = visibleNodeTextureOffsets.get(parent);
        if (parentOffset === undefined) continue;

        const parentOffsetToChild = i - parentOffset;

        offsetsToChild[parentOffset] = Math.min(offsetsToChild[parentOffset], parentOffsetToChild);

        data[parentOffset * 4 + 0] = data[parentOffset * 4 + 0] | (1 << index);
        data[parentOffset * 4 + 1] = offsetsToChild[parentOffset] >> 8;
        data[parentOffset * 4 + 2] = offsetsToChild[parentOffset] % 256;
      }

      const density = node.geometryNode.density;

      if (typeof density === 'number') {
        const lodOffset = Math.log2(density) / 2 - 1.5;

        const offsetUint8 = (lodOffset + 10) * 10;

        data[i * 4 + 3] = offsetUint8;
      } else {
        data[i * 4 + 3] = 100;
      }
    }

    if (measureTimings) {
      performance.mark('computeVisibilityTextureData-end');
      performance.measure('render.computeVisibilityTextureData', 'computeVisibilityTextureData-start', 'computeVisibilityTextureData-end');
    }

    return {
      data: data,
      offsets: visibleNodeTextureOffsets,
    };
  }

  deepestNodeAt(position: THREE.Vector3) {
    const toObjectSpace = this.matrixWorld.clone().invert();

    const objPos = position.clone().applyMatrix4(toObjectSpace);

    let current: PointCloudOctreeNode | PointCloudOctreeGeometryNode | null = this.root;
    while (true) {
      if (!current) break;
      let containingChild: PointCloudOctreeNode | PointCloudOctreeGeometryNode | null = null;

      const children = 'getChildren' in current ? current.getChildren() : [];
      for (const child of children) {
        if (child !== undefined) {
          if (child.getBoundingBox().containsPoint(objPos)) {
            containingChild = child;
          }
        }
      }

      if (containingChild !== null && containingChild instanceof PointCloudOctreeNode) {
        current = containingChild;
      } else {
        break;
      }
    }

    const deepest = current;

    return deepest;
  }

  nodesOnRay(nodes: PointCloudOctreeNode[], ray: THREE.Ray) {
    const nodesOnRay: PointCloudOctreeNode[] = [];

    const _ray = ray.clone();
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const sphere = node.getBoundingSphere().clone().applyMatrix4(this.matrixWorld);

      if (_ray.intersectsSphere(sphere)) {
        nodesOnRay.push(node);
      }
    }

    return nodesOnRay;
  }

  updateMatrixWorld(force?: boolean) {
    if (this.matrixAutoUpdate === true) this.updateMatrix();

    if (this.matrixWorldNeedsUpdate === true || force === true) {
      if (!this.parent) {
        this.matrixWorld.copy(this.matrix);
      } else {
        this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
      }

      this.matrixWorldNeedsUpdate = false;

      force = true;
    }
  }

  hideDescendants(object: THREE.Object3D) {
    const stack: THREE.Object3D[] = [];
    for (let i = 0; i < object.children.length; i++) {
      const child = object.children[i];
      if (child.visible) {
        stack.push(child);
      }
    }

    while (stack.length > 0) {
      const current = stack.shift();
      if (!current) continue;

      current.visible = false;

      for (let i = 0; i < current.children.length; i++) {
        const child = current.children[i];
        if (child.visible) {
          stack.push(child);
        }
      }
    }
  }

  moveToOrigin() {
    this.position.set(0, 0, 0);
    this.updateMatrixWorld(true);
    const box = this.boundingBox;
    const transform = this.matrixWorld;
    const tBox = Utils.computeTransformedBoundingBox(box, transform);
    this.position.set(0, 0, 0).sub(tBox.getCenter(new THREE.Vector3()));
  }

  moveToGroundPlane() {
    this.updateMatrixWorld(true);
    const box = this.boundingBox;
    const transform = this.matrixWorld;
    const tBox = Utils.computeTransformedBoundingBox(box, transform);
    this.position.y += -tBox.min.y;
  }

  getBoundingBoxWorld() {
    this.updateMatrixWorld(true);
    const box = this.boundingBox;
    const transform = this.matrixWorld;
    const tBox = Utils.computeTransformedBoundingBox(box, transform);

    return tBox;
  }

  getVisibleExtent() {
    return this.visibleBounds.applyMatrix4(this.matrixWorld);
  }

  intersectsPoint(position: THREE.Vector3) {
    const rootAvailable = this.pcoGeometry.root && this.pcoGeometry.root.geometry;

    if (!rootAvailable) {
      return false;
    }

    if (typeof this.signedDistanceField === 'undefined') {
      const resolution = 32;
      const field = new Float32Array(resolution ** 3).fill(Infinity);

      const geometry = this.pcoGeometry.root.geometry;
      if (!geometry) return false;
      const positions = geometry.attributes.position;
      const boundingBox = this.boundingBox;

      const n = positions.count;

      for (let i = 0; i < n; i = i + 3) {
        const x = (positions.array as Float32Array)[3 * i + 0];
        const y = (positions.array as Float32Array)[3 * i + 1];
        const z = (positions.array as Float32Array)[3 * i + 2];

        const ix = Math.floor(Math.min(resolution * (x / boundingBox.max.x), resolution - 1));
        const iy = Math.floor(Math.min(resolution * (y / boundingBox.max.y), resolution - 1));
        const iz = Math.floor(Math.min(resolution * (z / boundingBox.max.z), resolution - 1));

        const index = ix + iy * resolution + iz * resolution * resolution;

        field[index] = 0;
      }

      const sdf: SignedDistanceField = {
        resolution: resolution,
        field: field,
      };

      this.signedDistanceField = sdf;
    }

    {
      const sdf = this.signedDistanceField;
      const boundingBox = this.boundingBox;

      const toObjectSpace = this.matrixWorld.clone().invert();

      const objPos = position.clone().applyMatrix4(toObjectSpace);

      const resolution = sdf.resolution;
      const ix = Math.floor(resolution * (objPos.x / boundingBox.max.x));
      const iy = Math.floor(resolution * (objPos.y / boundingBox.max.y));
      const iz = Math.floor(resolution * (objPos.z / boundingBox.max.z));

      if (ix < 0 || iy < 0 || iz < 0) {
        return false;
      }
      if (ix >= resolution || iy >= resolution || iz >= resolution) {
        return false;
      }

      const index = ix + iy * resolution + iz * resolution * resolution;

      const value = sdf.field[index];

      if (value === 0) {
        return true;
      }
    }

    return false;
  }

  /**
   *
   *
   *
   * params.pickWindowSize:	Look for points inside a pixel window of this size.
   *							Use odd values: 1, 3, 5, ...
   *
   *
   * TODO: only draw pixels that are actually read with readPixels().
   *
   */
  pick(viewer: PickViewer, camera: THREE.Camera, ray: THREE.Ray, params: PickParams = {}) {
    const renderer = viewer.renderer;
    const pRenderer = viewer.pRenderer;

    performance.mark('pick-start');

    const getVal = <T>(a: T | undefined, b: T): T => (a !== undefined ? a : b);

    const pickWindowSize = getVal(params.pickWindowSize, 65);

    const size = renderer.getSize(new THREE.Vector2());

    const width = Math.ceil(getVal(params.width, size.width));
    const height = Math.ceil(getVal(params.height, size.height));

    const pointSizeType = getVal(params.pointSizeType, this.material.pointSizeType);
    const pointSize = getVal(params.pointSize, this.material.size);

    const nodes = this.nodesOnRay(this.visibleNodes, ray);

    if (nodes.length === 0) {
      return null;
    }

    if (!this.pickState) {
      const scene = new THREE.Scene();

      const material = new PointCloudMaterial();
      material.activeAttributeName = 'indices';

      const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
      });

      this.pickState = {
        renderTarget: renderTarget,
        material: material,
        scene: scene,
      };
    }

    const pickState = this.pickState;
    const pickMaterial = pickState.material;

    // update pick material
    pickMaterial.pointSizeType = pointSizeType;
    pickMaterial.shape = PointShape.PARABOLOID;

    pickMaterial.uniforms.uFilterReturnNumberRange.value = this.material.uniforms.uFilterReturnNumberRange.value;
    pickMaterial.uniforms.uFilterNumberOfReturnsRange.value = this.material.uniforms.uFilterNumberOfReturnsRange.value;
    pickMaterial.uniforms.uFilterGPSTimeClipRange.value = this.material.uniforms.uFilterGPSTimeClipRange.value;
    pickMaterial.uniforms.uFilterPointSourceIDClipRange.value = this.material.uniforms.uFilterPointSourceIDClipRange.value;

    pickMaterial.activeAttributeName = 'indices';

    pickMaterial.size = pointSize;
    pickMaterial.uniforms.minSize.value = this.material.uniforms.minSize.value;
    pickMaterial.uniforms.maxSize.value = this.material.uniforms.maxSize.value;
    pickMaterial.classification = this.material.classification;
    pickMaterial.recomputeClassification();

    if (params.pickClipped) {
      pickMaterial.clipBoxes = [];
      pickMaterial.clusteredSegments = [];
      pickMaterial.clipPlanes = [];
      pickMaterial.setClipSpheres([]);
    } else {
      const pickSegments = this.material.isolatedClipSegments();
      const pickClipBoxes = this.material.isolatedClipBoxes();
      const pickPlanes = this.material.isolatedClipPlanes();
      pickMaterial.setClipBoxes(pickClipBoxes);
      pickMaterial.setClusteredSegments(pickSegments);
      pickMaterial.setClipPlanes(pickPlanes);
      pickMaterial.setClipSpheres(this.material.isolatedClipSpheres());
    }

    pickMaterial.heightMin = this.material.heightMin;
    pickMaterial.heightMax = this.material.heightMax;
    pickMaterial.intensityMin = this.material.intensityMin;
    pickMaterial.intensityMax = this.material.intensityMax;

    this.updateMaterial(pickMaterial, nodes, camera as THREE.PerspectiveCamera, renderer);

    pickState.renderTarget.setSize(width, height);

    const pixelPos = new THREE.Vector2(params.x, params.y);

    const gl = renderer.getContext();
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(Math.floor(pixelPos.x - (pickWindowSize - 1) / 2), Math.floor(pixelPos.y - (pickWindowSize - 1) / 2), Math.floor(pickWindowSize), Math.floor(pickWindowSize));

    renderer.state.buffers.depth.setTest(pickMaterial.depthTest);
    renderer.state.buffers.depth.setMask(pickMaterial.depthWrite);
    renderer.state.setBlending(THREE.NoBlending);

    {
      // RENDER
      renderer.setRenderTarget(pickState.renderTarget);
      gl.clearColor(0, 0, 0, 0);
      renderer.clear(true, true, true);

      const tmp = this.material;
      this.material = pickMaterial;

      pRenderer.renderOctree(this, nodes, camera, pickState.renderTarget);

      this.material = tmp;
    }

    const clamp = (number: number, min: number, max: number) => Math.min(Math.max(min, number), max);

    const x = Math.floor(clamp(pixelPos.x - (pickWindowSize - 1) / 2, 0, width));
    const y = Math.floor(clamp(pixelPos.y - (pickWindowSize - 1) / 2, 0, height));
    const w = Math.floor(Math.min(x + pickWindowSize, width) - x);
    const h = Math.floor(Math.min(y + pickWindowSize, height) - y);

    if (w <= 0 || h <= 0) {
      renderer.setRenderTarget(null);
      renderer.state.reset();
      renderer.setScissorTest(false);
      gl.disable(gl.SCISSOR_TEST);
      return null;
    }

    const pixelCount = w * h;
    const buffer = new Uint8Array(4 * pixelCount);

    gl.readPixels(x, y, w, h, gl.RGBA, gl.UNSIGNED_BYTE, buffer);

    renderer.setRenderTarget(null);
    renderer.state.reset();
    renderer.setScissorTest(false);
    gl.disable(gl.SCISSOR_TEST);

    const pixels = buffer;
    const ibuffer = new Uint32Array(buffer.buffer);

    // Center of the pick in local read-window coords (pixelPos is in framebuffer coords;
    // x/y may have been clamped at the edges, so the center is not always (w-1)/2).
    const centerU = pixelPos.x - x;
    const centerV = pixelPos.y - y;

    // find closest hit inside pixelWindow boundaries
    const hits: PickHit[] = [];
    for (let u = 0; u < w; u++) {
      for (let v = 0; v < h; v++) {
        const offset = u + v * w;
        const distance = Math.pow(u - centerU, 2) + Math.pow(v - centerV, 2);

        const pcIndex = pixels[4 * offset + 3];
        pixels[4 * offset + 3] = 0;
        const pIndex = ibuffer[offset];

        if (!(pcIndex === 0 && pIndex === 0) && pcIndex !== undefined && pIndex !== undefined) {
          const hit: PickHit = {
            pIndex: pIndex,
            pcIndex: pcIndex,
            distanceToCenter: distance,
          };

          if (params.all) {
            hits.push(hit);
          } else {
            if (hits.length > 0) {
              if (distance < hits[0].distanceToCenter) {
                hits[0] = hit;
              }
            } else {
              hits.push(hit);
            }
          }
        }
      }
    }

    for (const hit of hits) {
      const point: PickPoint = { attributes: {} };

      if (!nodes[hit.pcIndex]) {
        return null;
      }

      const node = nodes[hit.pcIndex];
      if (!node.sceneNode || !node.geometryNode.geometry) return null;
      const pc = node.sceneNode;
      const geometry = node.geometryNode.geometry;

      for (const attributeName in geometry.attributes) {
        const attribute = geometry.attributes[attributeName] as PotreeBufferAttribute;

        if (attributeName === 'position') {
          const x = (attribute.array as Float32Array)[3 * hit.pIndex + 0];
          const y = (attribute.array as Float32Array)[3 * hit.pIndex + 1];
          const z = (attribute.array as Float32Array)[3 * hit.pIndex + 2];

          const position = new THREE.Vector3(x, y, z);
          position.applyMatrix4(pc.matrixWorld);

          point.position = position;
          point.attributes[attributeName] = position;
        } else if (attributeName === 'indices') {
          // skip
        } else {
          let values: number[] = Array.from((attribute.array as Float32Array).slice(attribute.itemSize * hit.pIndex, attribute.itemSize * (hit.pIndex + 1)));

          if (attribute.potree) {
            const { scale, offset } = attribute.potree;
            values = values.map((v) => v / scale + offset);
          }

          point.attributes[attributeName] = values;
        }
      }
      // pythagoras
      point.pointCloudId = this.identifier;
      point.distanceToRay = hit.distanceToCenter;
      point.spacing = node.geometryNode.spacing;
      point.level = node.geometryNode.level;
      // pythagoras
      hit.point = point;
    }

    performance.mark('pick-end');
    performance.measure('pick', 'pick-start', 'pick-end');

    if (params.all) {
      return hits.map((hit) => hit.point);
    } else {
      if (hits.length === 0) {
        return null;
      } else {
        return hits[0].point;
      }
    }
  }

  *getFittedBoxGen(boxNode: THREE.Object3D) {
    const shrinkedLocalBounds = new THREE.Box3();
    const worldToBox = boxNode.matrixWorld.clone().invert();

    for (const node of this.visibleNodes) {
      if (!node.sceneNode) {
        continue;
      }

      const buffer = node.geometryNode.buffer;

      const posOffset = buffer.offset('position');
      const stride = buffer.stride;
      const view = new DataView(buffer.data);

      const objectToBox = new THREE.Matrix4().multiplyMatrices(worldToBox, node.sceneNode.matrixWorld);

      const pos = new THREE.Vector4();
      for (let i = 0; i < buffer.numElements; i++) {
        const x = view.getFloat32(i * stride + posOffset + 0, true);
        const y = view.getFloat32(i * stride + posOffset + 4, true);
        const z = view.getFloat32(i * stride + posOffset + 8, true);

        pos.set(x, y, z, 1);
        pos.applyMatrix4(objectToBox);

        if (-0.5 < pos.x && pos.x < 0.5) {
          if (-0.5 < pos.y && pos.y < 0.5) {
            if (-0.5 < pos.z && pos.z < 0.5) {
              shrinkedLocalBounds.expandByPoint(pos as unknown as THREE.Vector3);
            }
          }
        }
      }

      yield;
    }

    const fittedPosition = shrinkedLocalBounds.getCenter(new THREE.Vector3()).applyMatrix4(boxNode.matrixWorld);

    const fitted = new THREE.Object3D();
    fitted.position.copy(fittedPosition);
    fitted.scale.copy(boxNode.scale);
    fitted.rotation.copy(boxNode.rotation);

    const ds = new THREE.Vector3().subVectors(shrinkedLocalBounds.max, shrinkedLocalBounds.min);
    fitted.scale.multiply(ds);

    yield fitted;
  }

  getFittedBox(boxNode: THREE.Object3D, maxLevel = Infinity) {
    maxLevel = Infinity;

    const shrinkedLocalBounds = new THREE.Box3();
    const worldToBox = boxNode.matrixWorld.clone().invert();

    for (const node of this.visibleNodes) {
      if (!node.sceneNode || (node.getLevel() ?? 0) > maxLevel) {
        continue;
      }

      const buffer = node.geometryNode.buffer;

      const posOffset = buffer.offset('position');
      const stride = buffer.stride;
      const view = new DataView(buffer.data);

      const objectToBox = new THREE.Matrix4().multiplyMatrices(worldToBox, node.sceneNode.matrixWorld);

      const pos = new THREE.Vector4();
      for (let i = 0; i < buffer.numElements; i++) {
        const x = view.getFloat32(i * stride + posOffset + 0, true);
        const y = view.getFloat32(i * stride + posOffset + 4, true);
        const z = view.getFloat32(i * stride + posOffset + 8, true);

        pos.set(x, y, z, 1);
        pos.applyMatrix4(objectToBox);

        if (-0.5 < pos.x && pos.x < 0.5) {
          if (-0.5 < pos.y && pos.y < 0.5) {
            if (-0.5 < pos.z && pos.z < 0.5) {
              shrinkedLocalBounds.expandByPoint(pos as unknown as THREE.Vector3);
            }
          }
        }
      }
    }

    const fittedPosition = shrinkedLocalBounds.getCenter(new THREE.Vector3()).applyMatrix4(boxNode.matrixWorld);

    const fitted = new THREE.Object3D();
    fitted.position.copy(fittedPosition);
    fitted.scale.copy(boxNode.scale);
    fitted.rotation.copy(boxNode.rotation);

    const ds = new THREE.Vector3().subVectors(shrinkedLocalBounds.max, shrinkedLocalBounds.min);
    fitted.scale.multiply(ds);

    return fitted;
  }

  get progress() {
    return this.visibleNodes.length / this.visibleGeometry.length;
  }

  find(name: string) {
    let node: PointCloudOctreeNode | PointCloudOctreeGeometryNode | null = null;
    for (const char of name) {
      if (char === 'r') {
        node = this.root as PointCloudOctreeNode;
      } else {
        if (node && 'children' in node) {
          node = (node.children as unknown as Record<string, PointCloudOctreeNode>)[char];
        }
      }
    }

    return node;
  }

  // @ts-expect-error - Override Object3D.visible property with accessor for event dispatch
  get visible() {
    return this._visible;
  }

  set visible(value: boolean) {
    if (value !== this._visible) {
      this._visible = value;

      // @ts-expect-error - custom event type used by Potree event system
      this.dispatchEvent({ type: 'visibility_changed', pointcloud: this });
    }
  }
}
